// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import ProductSearchScoreData from "@insite/content-library/Components/ProductSearchScoreData";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import ProductListProductImage from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ProductListProductImageStyles } from "@insite/content-library/Widgets/ProductList/ProductListProductImage";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Button } from "@insite/mobius/index";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SignInButton, { SignInButtonStyles } from "@werner/App/Components/Common/SignInButton";
import { xIsAuthenticated } from "@werner/App/Store/Selectors/GeneralSelectors";
import ProductListActions, { ProductListActionsStyles } from "@werner/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductInformation, {
    ProductListProductInformationStyles,
} from "@werner/Overrides/Widgets/ProductList/ProductListProductInformation";
import { color, ellipsis, mergeWithCss, sharedTextBadgeStyle } from "@werner/XDL/Utilities";
import React, { FC } from "react";
import { css } from "styled-components";

type Props = ProductCardSelections & HasProduct;

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    searchScoreDataGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
    // XNG-Changes
    container?: InjectableCss;
    imageSection?: InjectableCss;
    infoSection?: InjectableCss;
    actionsSection?: InjectableCss;
    productListProductImageStyles?: ProductListProductImageStyles;
    productListProductInformation?: ProductListProductInformationStyles;
    infoActionsWrapper?: InjectableCss;
    compareAction?: ProductListProductImageStyles;
    addToListAction?: ProductListActionsStyles;
    productListActions?: ProductListActionsStyles;
    signInButton?: SignInButtonStyles;
    productSearchScoreWrapper?: InjectableCss;
    viewOptionsButton?: ButtonPresentationProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 12px;
            opacity: 0.5;
        `,
    },
    container: {
        css: css`
            display: flex;
            border-bottom: 1px solid ${color("common.border")};
            position: relative;
            padding: 10px 0;
        `,
    },
    imageSection: {
        css: css`
            margin-right: 30px;
            width: 100%;
            max-width: 194px;
        `,
    },
    infoSection: {
        css: css`
            max-width: 490px;
            flex: 1 1 auto;
            margin-top: 24px;
            margin-bottom: 24px;
        `,
    },
    actionsSection: {
        css: css`
            max-width: 222px;
            flex: 1 1 auto;
            margin-left: auto;
            margin-top: 24px;
            margin-bottom: 24px;
        `,
    },
    productListProductImageStyles: {
        wrapper: {
            css: css`
                display: flex;
                align-items: center;
                padding: 9px;
            `,
        },
        productImage: {
            linkWrappingImage: {
                css: css`
                    width: 176px;
                    height: 176px;
                    background: ${color("common.white")};
                    justify-content: center;
                `,
            },
            textBadge: {
                ...mergeWithCss(sharedTextBadgeStyle, {
                    css: css`
                        width: 86px;
                        max-width: unset;
                        position: absolute;
                        top: 5px;
                        left: auto;
                        right: 5px;
                    `,
                }),
            },
        } as any,
    },
    productListProductInformation: {
        descriptionStyles: {
            productDetailLink: {
                color: "common.ui3",
                typographyProps: {
                    size: 18,
                    weight: "normal",
                    css: css`
                        line-height: 22px;
                        ${ellipsis(2)}
                    `,
                },
                css: css`
                    margin-bottom: 10px;
                `,
            },
        },
    },
    infoActionsWrapper: {
        css: css`
            display: flex;
            align-items: center;
            gap: 90px;
            margin-top: 30px;
        `,
    },
    compareAction: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                width: auto;
                height: auto;
            `,
        },
        compareCheckbox: {
            css: css`
                padding-top: 0;
            `,
        },
    },
    addToListAction: {
        wrapper: {
            css: css`
                display: flex;
                width: auto;
                flex-direction: row;
            `,
        },
        addToListWrapper: {
            css: css`
                margin-top: initial;
                min-height: initial;
                white-space: nowrap;
            `,
        },
    },
    productListActions: {
        wrapper: {
            css: css`
                display: flex;
                flex-direction: column;
                gap: 10px;
            `,
        },
        addToCartButton: {
            css: css`
                width: 100%;
            `,
        },
        price: {
            wrapper: {
                css: css`
                    margin-bottom: 12px;
                    text-align: right;
                `,
            },
            price: {
                priceText: {
                    size: 16,
                    weight: 500,
                },
                errorText: {
                    size: 14,
                    weight: "normal",
                    color: "danger",
                },
                signInText: {
                    size: 14,
                    weight: "normal",
                },
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 16,
                    weight: 500,
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
        },
    },
    signInButton: {
        button: {
            css: css`
                width: 100%;
            `,
        },
    },
    productSearchScoreWrapper: {
        css: css`
            margin-top: 10px;
        `,
    },
    viewOptionsButton: {
        css: css`
            width: 100%;
        `,
    },
};

const styles = productCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const { isAuthenticated } = useAppSelector(state => ({
        isAuthenticated: xIsAuthenticated(state),
    }));
    const history = useHistory();

    // XNG-Changes: show "view options" button when unauthenticated and product isVariantParent
    const isVariantParent = props.product.isVariantParent;

    const handleViewOptionsClick = () => {
        history.push(props.productInfo.productDetailPath);
    };

    // XNG-Changes: Update structure
    return (
        <StyledWrapper {...styles.container} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper {...styles.imageSection}>
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={false}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                    extendedStyles={styles.productListProductImageStyles}
                />
                {props.product.isSponsored && (
                    <Typography {...styles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.infoSection}>
                <ProductListProductInformation
                    showBrand={props.showBrand}
                    showTitle={props.showTitle}
                    showPartNumbers={props.showPartNumbers}
                    showAvailability={props.showAvailability}
                    showAttributes={props.showAttributes}
                    showImageBadges={props.showImageBadges}
                    maxImageBadges={props.maximumNumberOfImageBadges}
                    showTextBadges={props.showTextBadges}
                    maxTextBadges={props.maximumNumberOfTextBadges}
                    view="List"
                    extendedStyles={styles.productListProductInformation}
                />
                <StyledWrapper {...styles.infoActionsWrapper}>
                    {isAuthenticated && (
                        <ProductListActions
                            showPrice={false}
                            showUnitOfMeasure={false}
                            showAddToCartAndQty={false}
                            showAddToList={props.showAddToList}
                            view="List"
                            extendedStyles={styles.addToListAction}
                        />
                    )}
                    <ProductListProductImage
                        showImage={false}
                        showCompare={props.showCompare}
                        showImageBadge={false}
                        showTextBadge={false}
                        extendedStyles={styles.compareAction}
                    />
                </StyledWrapper>
                <StyledWrapper {...styles.productSearchScoreWrapper}>
                    <ProductSearchScoreData />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.actionsSection}>
                {!isAuthenticated && isVariantParent ? (
                    <Button onClick={handleViewOptionsClick} {...styles.viewOptionsButton}>
                        {translate("View Options")}
                    </Button>
                ) : isAuthenticated ? (
                    <ProductListActions
                        showPrice={props.showPrice}
                        showAddToList={false}
                        showAddToCartAndQty={true}
                        showUnitOfMeasure={true}
                        extendedStyles={styles.productListActions}
                        view="List"
                    />
                ) : (
                    <SignInButton labelOverride={translate("Sign In For Price")} extendedStyles={styles.signInButton} />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default withProduct(ProductListProductCard);
